import jwtDecode from "jwt-decode";
import { getPropertyById, getUserDetails } from "../../../../SOA/api";
import { getIncidentReportById } from "./Api";

const toLocalISO = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const pad = (n) => String(n).padStart(2, "0");
    return (
      d.getFullYear() +
      "-" + pad(d.getMonth() + 1) +
      "-" + pad(d.getDate()) +
      "T" + pad(d.getHours()) +
      ":" + pad(d.getMinutes())
    );
  } catch {
    return null;
  }
};

const toLocalDateOnly = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const pad = (n) => String(n).padStart(2, "0");
    return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
  } catch {
    return null;
  }
};

export const defaultPerson = {
  type: "",
  specifyType: "",
  firstName: "",
  middleName: "",
  lastName: "",
  email: "",
  phoneNumbers: [""],
  unitNo: "",
  homeAddress: "",
  notificationToPropertyManagerDateTime: null,
  statement: "",
  attachments: [],
  witnesses: [],
  signature: { signature: "", date: null },
};

export const defaultWitness = {
  firstName: "",
  middleName: "",
  lastName: "",
  address: "",
  phoneNo: "",
  email: "",
};

export const defaultNotificationEmail = {
  firstName: "",
  lastName: "",
  email: "",
  managersNote: "",
};

export const defaultIncidentReport = {
  notification: {
    managerReporting: "",
    managerEmail: "",
    managerPhoneNo: "",
    personsInvolved: [{ ...defaultPerson }],
  },
  investigation: {
    occurrenceDateTime: null,
    notificationToPropertyManagerDateTime: null,
    weatherConditions: [],
    weatherConditionsOther: "",
    temperatureCelsius: null,
    environmentConditions: [],
    visibility: [],
    statement: "",
    witnesses: [],
    attachments: [],
    leaseDocument: "",
    carInsuranceDocument: "",
    homeInsuranceDocument: "",
    investigationDoneBy: { name: "", date: null },
  },
  notificationEmails: [],
  propertyDetails: {
    propertyId: "",
    buildingName: "",
  },
  status: "Open",
  followUpRequired: null,
  comments: "",
};

const mapWitness = (w) => ({
  firstName: w.firstName || "",
  middleName: w.middleName || "",
  lastName: w.lastName || "",
  address: w.address || "",
  phoneNo: w.phoneNo || "",
  email: w.email || "",
});

const mapPerson = (p) => ({
  type: p.type || "",
  specifyType: p.specifyType || "",
  firstName: p.firstName || "",
  middleName: p.middleName || "",
  lastName: p.lastName || "",
  email: p.email || "",
  phoneNumbers: Array.isArray(p.phoneNumbers) && p.phoneNumbers.length > 0 ? p.phoneNumbers : [""],
  unitNo: p.unitNo || "",
  homeAddress: p.homeAddress || "",
  notificationToPropertyManagerDateTime: toLocalISO(p.notificationToPropertyManagerDateTime),
  statement: p.statement || "",
  attachments: Array.isArray(p.attachments) ? p.attachments : [],
  witnesses: Array.isArray(p.witnesses) ? p.witnesses.map(mapWitness) : [],
  signature: {
    signature: p.signature?.signature || "",
    date: toLocalDateOnly(p.signature?.date),
  },
});

const mapNotificationEmail = (n) =>
  typeof n === "string"
    ? { firstName: "", lastName: "", email: n, managersNote: "" }
    : {
        firstName: n.firstName || "",
        lastName: n.lastName || "",
        email: n.email || "",
        managersNote: n.managersNote || "",
      };

export const getPropertyDetailsForReport = async (propertyId) => {
  if (!propertyId) throw new Error("Property ID is required");

  const token = localStorage.getItem("accessToken");
  const user = token ? jwtDecode(token) : null;

  if (!user?.id || !user?.role) throw new Error("User details missing from token");

  const property = await getPropertyById(propertyId);
  if (!property) throw new Error("Property not found");

  let currentUserDetails = null;
  if (["technical staff", "manager", "company"].includes(user.role)) {
    try {
      currentUserDetails = await getUserDetails(user.id, user.role);
    } catch { }
  }

  const name = currentUserDetails
    ? `${currentUserDetails.first_name || currentUserDetails.firstname || ""} ${currentUserDetails.last_name || currentUserDetails.lastname || ""}`.trim()
    : "Unknown User";

  return {
    property,
    currentUser: { id: user.id, role: user.role, name },
  };
};

export const getIncidentReportInitialValues = async (id) => {
  if (!id) return defaultIncidentReport;

  try {
    const report = await getIncidentReportById(id);
    const inv = report.investigation || {};

    return {
      _id: report._id,

      notification: {
        managerReporting: report.notification?.managerReporting || "",
        managerEmail: report.notification?.managerEmail || "",
        managerPhoneNo: report.notification?.managerPhoneNo || "",
        personsInvolved:
          Array.isArray(report.notification?.personsInvolved) &&
            report.notification.personsInvolved.length > 0
            ? report.notification.personsInvolved.map(mapPerson)
            : [mapPerson({})],
      },

      investigation: {
        occurrenceDateTime: toLocalISO(inv.occurrenceDateTime),
        notificationToPropertyManagerDateTime: toLocalISO(inv.notificationToPropertyManagerDateTime),
        weatherConditions: Array.isArray(inv.weatherConditions) ? inv.weatherConditions : [],
        weatherConditionsOther: inv.weatherConditionsOther || "",
        temperatureCelsius: inv.temperatureCelsius ?? null,
        environmentConditions: Array.isArray(inv.environmentConditions) ? inv.environmentConditions : [],
        visibility: Array.isArray(inv.visibility) ? inv.visibility : [],
        statement: inv.statement || "",
        witnesses: Array.isArray(inv.witnesses) ? inv.witnesses.map(mapWitness) : [],
        attachments: inv.attachments || [],
        leaseDocument: inv.leaseDocument || "",
        carInsuranceDocument: inv.carInsuranceDocument || "",
        homeInsuranceDocument: inv.homeInsuranceDocument || "",
        investigationDoneBy: {
          name: inv.investigationDoneBy?.name || "",
          date: toLocalDateOnly(inv.investigationDoneBy?.date),
        },
      },

      notificationEmails: Array.isArray(report.notificationEmails)
        ? report.notificationEmails.map(mapNotificationEmail)
        : [],

      completedBy: report.completedBy || null,

      propertyDetails: {
        propertyId: report.propertyDetails?.propertyId?.toString() || "",
        buildingName: report.propertyDetails?.buildingName || "",
      },

      status: report.status || "Open",
      followUpRequired: typeof report.followUpRequired === 'boolean' ? report.followUpRequired : null,
      comments: report.comments || "",
    };
  } catch (error) {
    console.error("Failed to load incident report:", error);
    return defaultIncidentReport;
  }
};

export default defaultIncidentReport;